import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Modal,
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  PlayCircleFilled as PlayCircleFilledIcon,
} from '@mui/icons-material';
import VideoContent from '../UIKit/VideoContent';
import { useTranslation } from 'react-i18next';

const VideoModal = ({ open, onClose, videos }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isVeryShort, setIsVeryShort] = useState(false);
  const { t } = useTranslation('common');

  const currentVideo = useMemo(
    () => videos[currentIndex] || null,
    [videos, currentIndex]
  );

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : videos.length - 1));
  }, [videos.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < videos.length - 1 ? prev + 1 : 0));
  }, [videos.length]);

  useEffect(() => {
    const checkHeight = () =>
      setIsVeryShort(isMobile && window.innerHeight < 500);
    checkHeight();
    window.addEventListener('resize', checkHeight);
    return () => window.removeEventListener('resize', checkHeight);
  }, [isMobile]);

  return (
    <Modal
      open={open}
      aria-labelledby="video-modal-title"
      aria-describedby="video-modal-description"
      disableAutoFocus
      disableEnforceFocus
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 13000,
      }}
      BackdropProps={{ style: { background: 'rgba(0,0,0,0.85)' } }}
    >
      <Box
        sx={{
          background: `radial-gradient(circle at top right, rgba(152,152,152,0.15), transparent 70%),
                       radial-gradient(circle at bottom left, rgba(152,152,152,0.10), transparent 70%),
                       linear-gradient(135deg, transparent, rgba(152,152,152,0.12) 50%, transparent 100%)`,
          boxShadow:
            '0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)',
          backdropFilter: 'blur(40px)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          padding: theme.spacing(2),
          width: isMobile ? '100vw' : '90vw',
          maxWidth: isMobile ? '100vw' : '900px',
          maxHeight: isMobile ? '100vh' : '90vh',
          border: 'none',
          borderRadius: isVeryShort ? 0 : isMobile ? 1 : 4,
          p: isMobile ? 1 : 3,
        }}
      >
        {/* Верхняя панель */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            color: 'white',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 500, fontSize: isMobile ? '1rem' : '1.25rem' }}
          >
            {t('videoModal.counter', {
              current: currentIndex + 1,
              total: videos.length,
            })}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              ml: 2,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
            }}
            aria-label={t('videoModal.close')}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Центральная область с видео и навигацией */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            minHeight: 0,
            maxHeight: isMobile ? '80vh' : '60vh',
            flexDirection: isMobile ? 'column' : 'row',
            mb: isMobile ? 0 : 2,
          }}
        >
          {/* Левая стрелка (desktop) */}
          {!isMobile && videos.length > 1 && (
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 2,
                color: 'white',
                display: { xs: 'none', sm: 'flex' },
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
              }}
              aria-label={t('videoModal.previous')}
            >
              <NavigateBeforeIcon />
            </IconButton>
          )}

          {/* Видео */}
          <Box
            sx={{
              width: '100%',
              maxWidth: isMobile ? '100vw' : '800px',
              maxHeight: isMobile ? '70vh' : '60vh',
              mx: { xs: 0, sm: 4 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {currentVideo ? (
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '16/9',
                  maxHeight: isMobile ? '70vh' : '60vh',
                  maxWidth: `calc(${isMobile ? '70vh' : '60vh'} * 16 / 9)`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  margin: '0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <VideoContent
                  options={{ videoUrl: currentVideo }}
                  withMargin={false}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  textAlign: 'center',
                  p: 4,
                }}
              >
                <PlayCircleFilledIcon sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('videoModal.unavailable')}
                </Typography>
                <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                  {currentVideo}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Правая стрелка (desktop) */}
          {!isMobile && videos.length > 1 && (
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: '0',
                top: '50%',
                transform: 'translate(50%, -50%)',
                zIndex: 2,
                color: 'white',
                display: { xs: 'none', sm: 'flex' },
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
              }}
              aria-label={t('videoModal.next')}
            >
              <NavigateNextIcon />
            </IconButton>
          )}
        </Box>
        {/* Кнопки навигации под видео (mobile, portrait) */}
        {isMobile && videos.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <IconButton
              onClick={handlePrevious}
              sx={{
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
              aria-label={t('videoModal.previous')}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
              aria-label={t('videoModal.next')}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default VideoModal;
